import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, TextField, Popover, Typography, Checkbox, InputAdornment, CircularProgress, Button, Paper, IconButton, List, ListItem } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon, ArrowRight, BuildCircle, Search as SearchIcon, Clear } from '@mui/icons-material';
import { SimpleTreeView, TreeItem as MuiTreeItem, treeItemClasses } from '@mui/x-tree-view';
import { styled } from '@mui/material/styles';
import { useListItemCategorymasterQuery } from '../../../redux/apis/itemCategory.api';
import AddCategoryModal from './modals/addCategoryModal';
import DeleteCategoryModal from './modals/deleteCategoryModal';
import type { CreateItemCategorymasterMutation } from '../../../graphql/API';

const CustomExpandIcon = styled(ArrowRight)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '25px !important',
}));

const CustomCollapseIcon = styled(ArrowDropDownIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '25px !important',
}));

const CustomTreeItem = styled(MuiTreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginTop: -8,
    marginLeft: 13,
    paddingLeft: 8,
    position: 'relative',
  },
}));

type CategoryItem = {
  itemcategoryid: number;
  category: string;
  itemcategorygroupid: number;
  maincategory: string;
  children?: CategoryItem[];
};

type CategoryTreeItem = {
  value: [string, number];
  label: string;
  tagLabel: string;
  children?: CategoryTreeItem[];
};

interface CategoryAutocompleteProps {
  type: 'GOODS' | 'SERVICE';
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  // Optional props for advanced features (grid support, form integration)
  setState?: (prev: any) => any;
  setrow?: (prev: any) => any;
  index?: number;
  params?: any;
  setup?: string;
  label?: string;
  setCategoryData?: (data: CreateItemCategorymasterMutation['createItemCategorymaster']) => void;
  SetPrentId?: (id: number) => void;
}

const CategoryAutocomplete: React.FC<CategoryAutocompleteProps> = ({
  type,
  value,
  onChange,
  disabled = false,
  setState,
  setrow,
  index,
  params,
  setup,
  label = 'Under Category',
  setCategoryData,
  SetPrentId: setPrentIdCallback,
}) => {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listBoxRef = React.useRef<HTMLDivElement>(null);
  const highlightedItemRef = React.useRef<HTMLLIElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedSearchIndex, setHighlightedSearchIndex] = useState(-1);
  const [highlightedTreeIndex, setHighlightedTreeIndex] = useState(-1);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Modal states
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [deleteCategoryModalOpen, setDeleteCategoryModalOpen] = useState(false);
  const [categoryArr, setCategoryArr] = useState<
    Array<{
      label: string;
      value: number;
      itemcategoryid: number;
      category: string;
      itemcategorygroupid: number;
      maincategory: string;
      parent: string | null;
      children: any[];
    }>
  >([]);
  const [dataArr, setDataArr] = useState<any[]>([]);
  const [prtDetails, setPrtDetails] = useState<number | undefined>();
  const [parentId, setParentId] = useState<number | undefined>();
  const [newlyCreatedCategory, setNewlyCreatedCategory] = useState<{ id: number; name: string } | null>(null);

  const mainCategory = type === 'GOODS' ? 'Goods' : 'Services';

  const { data: categoryData, isLoading, isError, refetch } = useListItemCategorymasterQuery({ maincategory: mainCategory });

  // Find main category (first category with matching maincategory)
  const mainCategoryItem = useMemo(() => {
    if (!categoryData?.listItemCategorymaster) return null;

    const allItems = categoryData.listItemCategorymaster
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null && item !== undefined && item.itemcategoryid !== null && item.category !== null && item.maincategory !== null
      )
      .filter((item) => item.maincategory === mainCategory)
      .map((item) => ({
        itemcategoryid: item.itemcategoryid!,
        category: item.category!,
        itemcategorygroupid: item.itemcategorygroupid ?? 0,
        maincategory: item.maincategory!,
        children: [],
      }));

    return allItems[0] || null;
  }, [categoryData, mainCategory]);

  // Flatten tree for search
  const flattenTree = useCallback((tree: CategoryItem[]): CategoryItem[] => {
    const flat: CategoryItem[] = [];
    tree.forEach((node) => {
      flat.push(node);
      if (node.children && node.children.length > 0) {
        flat.push(...flattenTree(node.children));
      }
    });
    return flat;
  }, []);

  // Build tree structure from flat data
  const buildTree = useCallback((items: CategoryItem[]): CategoryItem[] => {
    const itemMap = new Map<number, CategoryItem>();
    const tree: CategoryItem[] = [];

    // Create map of all items
    items.forEach((item) => {
      itemMap.set(item.itemcategoryid, { ...item, children: [] });
    });

    // Build tree structure
    items.forEach((item) => {
      const node = itemMap.get(item.itemcategoryid)!;
      if (item.itemcategorygroupid === 0) {
        tree.push(node);
      } else {
        const parent = itemMap.get(item.itemcategorygroupid);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(node);
        }
      }
    });

    return tree;
  }, []);

  // Transform tree data for TreeView component
  const transformDataForTree = useCallback((data: CategoryItem[], allFlatItems: CategoryItem[]): CategoryTreeItem[] => {
    // Build hierarchical label
    const buildLabel = (cat: CategoryItem, allItems: CategoryItem[]): string => {
      if (cat.itemcategorygroupid === 0) {
        return cat.category;
      }
      const parent = allItems.find((i) => i.itemcategoryid === cat.itemcategorygroupid);
      if (parent) {
        return `${buildLabel(parent, allItems)} > ${cat.category}`;
      }
      return cat.category;
    };

    return data.map((item) => {
      const tagLabel = buildLabel(item, allFlatItems);

      return {
        value: [item.category, item.itemcategoryid],
        label: item.category,
        tagLabel,
        children: item.children && item.children.length > 0 ? transformDataForTree(item.children, allFlatItems) : undefined,
      };
    });
  }, []);

  // Process category data
  const processedData = useMemo(() => {
    if (!categoryData?.listItemCategorymaster) return [];

    const allItems = categoryData.listItemCategorymaster
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null &&
          item !== undefined &&
          item.itemcategoryid !== null &&
          item.itemcategoryid !== undefined &&
          item.category !== null &&
          item.category !== undefined
      )
      .map(
        (item): CategoryItem => ({
          itemcategoryid: item.itemcategoryid!,
          category: item.category!,
          itemcategorygroupid: item.itemcategorygroupid ?? 0,
          maincategory: item.maincategory ?? '',
          children: [],
        })
      );

    const filteredItems = allItems.filter((item) => item.maincategory === mainCategory);
    const tree = buildTree(filteredItems);
    return transformDataForTree(tree, filteredItems);
  }, [categoryData, mainCategory, buildTree, transformDataForTree]);

  // Build category array for DeleteCategoryModal
  const buildCategoryArray = useCallback(() => {
    if (!categoryData?.listItemCategorymaster) {
      setCategoryArr([]);
      setDataArr([]);
      return;
    }

    const allItems = categoryData.listItemCategorymaster
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null && item !== undefined && item.itemcategoryid !== null && item.category !== null
      )
      .map((item) => ({
        label: item.category!,
        value: item.itemcategoryid!,
        itemcategoryid: item.itemcategoryid!,
        category: item.category!,
        itemcategorygroupid: item.itemcategorygroupid ?? 0,
        maincategory: item.maincategory ?? '',
        children: [], // Add children array for tree structure
        parent: null as string | null, // Will be set by DeleteCategoryModal's addParentKey
      }));

    const filteredItems = allItems.filter((item) => item.maincategory === mainCategory);
    setCategoryArr(filteredItems);

    // Build tree data for AddCategory modal
    const tree = buildTree(filteredItems);
    const treeData = transformDataForTree(tree, filteredItems);
    setDataArr(treeData);
  }, [categoryData, mainCategory, buildTree, transformDataForTree]);

  // Refetch categories after add/edit/delete
  const getCategory = useCallback(async () => {
    await refetch();
    buildCategoryArray();
  }, [refetch, buildCategoryArray]);

  // Initialize category arrays
  useEffect(() => {
    buildCategoryArray();
  }, [buildCategoryArray]);

  // Flatten processed data for search
  const flatData = useMemo(() => {
    const flatten = (items: CategoryTreeItem[]): CategoryTreeItem[] => {
      const result: CategoryTreeItem[] = [];
      items.forEach((item) => {
        result.push(item);
        if (item.children && item.children.length > 0) {
          result.push(...flatten(item.children));
        }
      });
      return result;
    };
    return flatten(processedData);
  }, [processedData]);

  // Clear newly created category once it appears in the refetched list
  useEffect(() => {
    if (newlyCreatedCategory && flatData.some((item) => item.value[1] === newlyCreatedCategory.id)) {
      setNewlyCreatedCategory(null);
    }
  }, [newlyCreatedCategory, flatData]);

  // If selected category was deleted, clear stale selected value
  useEffect(() => {
    if (value === null || isLoading) return;

    const isValueAvailable = flatData.some((item) => item.value[1] === value);
    const isTemporaryNewCategory = newlyCreatedCategory?.id === value;

    if (!isValueAvailable && !isTemporaryNewCategory) {
      onChange(null);
    }
  }, [value, isLoading, flatData, newlyCreatedCategory, onChange]);

  // Auto-select / auto-correct value when type or data changes
  useEffect(() => {
    if (isLoading) return;
    if (!mainCategoryItem) return;
    // Removed disabled check - allow auto-selection even when disabled so data can be displayed
    // Wait for flatData to be populated before auto-selecting
  

    // Check whether current value belongs to this mainCategory
    const isInCurrentMain = flatData.some((item) => item.value[1] === value);

    // If no value selected, or value belongs to previous mainCategory, auto-select root
    // But don't override if value is set but not yet in list (e.g., newly created category)
    if (!isInCurrentMain && value === null) {
      onChange(mainCategoryItem.itemcategoryid);
    }
  }, [type, isLoading, mainCategoryItem, flatData, value, onChange]);

  // Find selected item
  const selectedItem = useMemo(() => {
    if (value === null) return null;
    const found = flatData.find((item) => item.value[1] === value);
    // If not found but we have a newly created category with this ID, use it temporarily
    if (!found && newlyCreatedCategory && newlyCreatedCategory.id === value) {
      return {
        value: [newlyCreatedCategory.name, newlyCreatedCategory.id],
        label: newlyCreatedCategory.name,
        tagLabel: newlyCreatedCategory.name,
      } as CategoryTreeItem;
    }
    return found || null;
  }, [value, flatData, newlyCreatedCategory]);
  // Find all category IDs to expand all nodes
  const getAllCategoryIds = useCallback((items: CategoryTreeItem[]): string[] => {
    const ids: string[] = [];
    items.forEach((item) => {
      ids.push(String(item.value[1]));
      if (item.children && item.children.length > 0) {
        ids.push(...getAllCategoryIds(item.children));
      }
    });
    return ids;
  }, []);

  // Find parent chain for expanding tree
  useEffect(() => {
    if (selectedItem) {
      const findParentChain = (item: CategoryTreeItem, allItems: CategoryTreeItem[]): string[] => {
        const chain: string[] = [String(item.value[1])];
        // Find parent by checking if any item has this as a child
        const parent = allItems.find((i) => i.children?.some((child) => child.value[1] === item.value[1]));
        if (parent) {
          chain.unshift(...findParentChain(parent, allItems));
        }
        return chain;
      };
      const parentChain = findParentChain(selectedItem, flatData);
      // When setup === 'NotNeed', keep all categories expanded to show the full hierarchy
      if (setup === 'NotNeed') {
        setExpandedItems(getAllCategoryIds(processedData));
      } else {
        setExpandedItems(parentChain);
      }
    } else {
      // When no item is selected (e.g., in Add Category modal), expand all categories to show nested children
      setExpandedItems(getAllCategoryIds(processedData));
    }
  }, [selectedItem, flatData, processedData, getAllCategoryIds, setup]);

  // Filter data based on search - when searching, show flat list; otherwise show tree
  const filteredData = useMemo(() => {
    if (!searchTerm) return processedData;
    return processedData; // Keep tree structure for non-search mode
  }, [searchTerm, processedData]);

  // Flatten tree in display order (only visible nodes: root + children of expanded)
  const flattenedVisibleTreeData = useMemo(() => {
    const flattenVisible = (items: CategoryTreeItem[], expanded: string[]): CategoryTreeItem[] => {
      const result: CategoryTreeItem[] = [];
      items.forEach((item) => {
        result.push(item);
        if (item.children?.length && expanded.includes(String(item.value[1]))) {
          result.push(...flattenVisible(item.children, expanded));
        }
      });
      return result;
    };
    return flattenVisible(filteredData, expandedItems);
  }, [filteredData, expandedItems]);

  // Filter flat data for search results (like CategoryDropDown.jsx)
  const filteredFlatData = useMemo(() => {
    if (!searchTerm) return [];
    const searchLower = searchTerm.toLowerCase();
    return flatData.filter((item) => item.label.toLowerCase().indexOf(searchLower) >= 0);
  }, [searchTerm, flatData]);

  // When search results change, set highlighted index to first item or -1
  useEffect(() => {
    if (filteredFlatData.length > 0) {
      setHighlightedSearchIndex((prev) => (prev >= filteredFlatData.length ? filteredFlatData.length - 1 : prev < 0 ? 0 : prev));
    } else {
      setHighlightedSearchIndex(-1);
    }
  }, [searchTerm, filteredFlatData.length]);

  // Scroll highlighted search result into view
  useEffect(() => {
    if (searchTerm && highlightedSearchIndex >= 0 && highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [searchTerm, highlightedSearchIndex]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(triggerRef.current ?? event.currentTarget);
    setSearchTerm('');
    setHighlightedSearchIndex(-1);
    setHighlightedTreeIndex(-1);
  };
  
  const open = Boolean(anchorEl);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || isLoading) return;
    const openKeys = ['ArrowDown', 'ArrowUp', ' ', 'Enter'];
    if (!open && openKeys.includes(event.key)) {
      event.preventDefault();
      setAnchorEl(triggerRef.current);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
    setHighlightedSearchIndex(-1);
    setHighlightedTreeIndex(-1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' && filteredFlatData.length > 0) {
      event.preventDefault();
      setHighlightedSearchIndex(0);
      listBoxRef.current?.focus();
    }
  };

  const handleSelect = useCallback(
    (item: CategoryTreeItem, event?: React.MouseEvent | React.ChangeEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // When setup === 'NotNeed' (used in Add Category modal), always set the value (don't toggle)
      const newValue = setup === 'NotNeed' ? item.value[1] : item.value[1] === value ? null : item.value[1];

      // Grid support
      if (setrow && params && index !== undefined) {
        setrow((prevArray: any[]) => {
          const newArray = [...prevArray];
          const firstObject = { ...newArray[index] };
          firstObject.category = searchTerm === '' ? [[item.value[0]], item.value] : [[item.label], [item.label, item.value]];
          newArray[index] = firstObject;
          return newArray;
        });

        if (params.api) {
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: searchTerm === '' ? item.value : [item.label, item.value],
          });
        }
      }

      onChange(newValue);
      handleClose();
    },
    [value, onChange, setrow, params, index, searchTerm, setup]
  );

  const handleListBoxKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (searchTerm) {
      if (filteredFlatData.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedSearchIndex((prev) => Math.min(prev + 1, filteredFlatData.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedSearchIndex((prev) => Math.max(prev - 1, 0));
      } else if (event.key === 'Enter' && highlightedSearchIndex >= 0 && filteredFlatData[highlightedSearchIndex]) {
        event.preventDefault();
        handleSelect(filteredFlatData[highlightedSearchIndex]);
      }
      return;
    }
    // No search: tree view keyboard navigation
    if (flattenedVisibleTreeData.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedTreeIndex((prev) => Math.min(prev + 1, flattenedVisibleTreeData.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedTreeIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && highlightedTreeIndex >= 0 && flattenedVisibleTreeData[highlightedTreeIndex]) {
      event.preventDefault();
      handleSelect(flattenedVisibleTreeData[highlightedTreeIndex]);
    }
  };

  

  const highlightedTreeId = !searchTerm && highlightedTreeIndex >= 0 && flattenedVisibleTreeData[highlightedTreeIndex]
    ? flattenedVisibleTreeData[highlightedTreeIndex].value[1]
    : null;

  // When popover opens with no search, focus list box and highlight first tree item
  useEffect(() => {
    if (open && !searchTerm && flattenedVisibleTreeData.length > 0) {
      setHighlightedTreeIndex(0);
      const timer = setTimeout(() => listBoxRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [open, searchTerm, flattenedVisibleTreeData.length]);

  // Recursive tree item component
  const RecursiveTreeItem: React.FC<{ item: CategoryTreeItem; highlightedTreeId?: number | null }> = ({ item, highlightedTreeId: highlightedId }) => {
    const isSelected = value === item.value[1];
    const isHighlighted = highlightedId !== undefined && highlightedId !== null && item.value[1] === highlightedId;

    return (
      <CustomTreeItem
        itemId={String(item.value[1])}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '13px',
              ...(isHighlighted ? { backgroundColor: 'action.hover' } : {}),
            }}
          >
            <Checkbox
              checked={isSelected}
              size="small"
              sx={{ fontSize: '13px' }}
              onChange={(e) => {
                e.stopPropagation();
                handleSelect(item, e);
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(item, e);
              }}
            />
            <Typography
              sx={{
                fontSize: '13px',
                width: '150px',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 2,
                lineHeight: '1.5',
                maxHeight: '3em',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(item, e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {item.label}
            </Typography>
          </Box>
        }
      >
        {item.children?.map((child) => (
          <RecursiveTreeItem key={child.value[1]} item={child} highlightedTreeId={highlightedTreeId} />
        ))}
      </CustomTreeItem>
    );
  };

  if (isError) {
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value="Error loading categories"
        error
        disabled
        sx={{
          '& .MuiInputBase-root': {
            paddingTop: '4px',
            paddingBottom: '4px',
            minHeight: '40px',
          },
        }}
      />
    );
  }

  return (
    <Box
      ref={triggerRef}
      sx={{
        width: '100%',
        outline: 'none',
        pointerEvents: 'auto',
      }}
    >
      <TextField
        fullWidth
        size="small"
        label={label}
        inputRef={inputRef}
        InputLabelProps={{
          shrink: true, // static label on top
        }}
        value={selectedItem?.tagLabel || ''}
        onClick={disabled ? undefined : handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled || isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{isLoading ? <CircularProgress size={20} /> : <ArrowDropDownIcon />}</InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-root': {
            // paddingTop: '4px',
            // paddingBottom: '4px',
            minHeight: '24px',
          },
          '& .MuiInputBase-input': {
            textDecoration: 'none !important',
            textUnderlineOffset: 'none',
            textDecorationLine: 'none',
            textTransform: 'none',
            fontSize: '13px',
          },
          '& .MuiInputLabel-root': { fontSize: '13px' },
          // Ensure text is visible when disabled
          '& .MuiInputBase-root.Mui-disabled': {
            opacity: 1,
            '& .MuiInputBase-input': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important',
              color: 'rgba(0, 0, 0, 0.87) !important',
            },
          },
        }}
        inputProps={{
          spellCheck: false,
          autoComplete: 'off',
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            maxHeight: '350px',
            minWidth: '250px',
          },
        }}
      >
        {/* Search Field - Only show when there are more than 5 items (like CategoryDropDown.jsx) */}
        {flatData.length > 5 && (
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Paper
              component="form"
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <TextField
                size="small"
                placeholder="Search category..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                        sx={{ p: 0 }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-input': {
                    fontSize: '13px',
                    py: '4px',
                  },
                }}
                autoFocus
              />
            </Paper>
          </Box>
        )}
        <Box
          ref={listBoxRef}
          tabIndex={-1}
          sx={{
            maxHeight: '250px',
            minHeight: '50px',
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: '250px',
            position: 'relative',
            outline: 'none',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
          onKeyDown={handleListBoxKeyDown}
          onMouseDown={(e) => {
            // Allow clicks to work properly in the scrollable area
            e.stopPropagation();
          }}
        >
          {searchTerm ? (
            // Show flat list when searching (like CategoryDropDown.jsx)
            filteredFlatData.length === 0 ? (
              <Typography variant="body1" sx={{ pl: 2, pt: 1 }}>
                No options
              </Typography>
            ) : (
              <List sx={{ minHeight: '200px' }}>
                {filteredFlatData.map((item, index) => {
                  const isSelected = value === item.value[1];
                  const isHighlighted = index === highlightedSearchIndex;
                  return (
                    <ListItem
                      key={item.value[1]}
                      ref={isHighlighted ? highlightedItemRef : null}
                      sx={{
                        mt: 0,
                        mb: 0,
                        pt: 0,
                        pb: 0,
                        p: 0,
                        pl: 2,
                        cursor: 'pointer',
                        backgroundColor: isHighlighted ? 'action.hover' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelect(item, e);
                      }}
                    >
                      <Checkbox
                        sx={{ fontSize: '10px', padding: '2px' }}
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelect(item, e);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(item, e);
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '13px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          WebkitLineClamp: 2,
                          lineHeight: '1.5',
                          maxHeight: '3em',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelect(item, e);
                        }}
                      >
                        {item.tagLabel || item.label}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            )
          ) : (
            // Show tree structure when not searching
            filteredData.length === 0 ? (
              <Typography variant="body1" sx={{ pl: 2, pt: 1 }}>
                No options
              </Typography>
            ) : (
              <SimpleTreeView
                defaultExpandedItems={expandedItems}
                slots={{
                  expandIcon: CustomExpandIcon,
                  collapseIcon: CustomCollapseIcon,
                }}
                sx={{
                  overflow: 'visible',
                  minHeight: 50,
                  flexGrow: 1,
                  maxWidth: '100%',
                  width: '100%',
                }}
              >
                {filteredData.map((item) => (
                  <RecursiveTreeItem key={item.value[1]} item={item} highlightedTreeId={highlightedTreeId} />
                ))}
              </SimpleTreeView>
            )
          )}
        </Box>
        <Box
          sx={{
            boxShadow: '0px -4px 10px -2px rgba(0, 0, 0, 0.2)',
            width: '100%',
          }}
        >
          {setup === undefined && (
            <Button
              className="dropDownBtncolor"
              sx={{ whiteSpace: 'nowrap', m: 0 }}
              startIcon={<BuildCircle />}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteCategoryModalOpen(true);
              }}
            >
              Add Category
            </Button>
          )}
        </Box>
      </Popover>

      {/* Delete Category Modal */}
      {deleteCategoryModalOpen && (
        <DeleteCategoryModal
          index={index}
          setrow={setrow}
          setState={setState}
          open={deleteCategoryModalOpen}
          setOpen={setDeleteCategoryModalOpen}
          params={params}
          setCategoryArr={setCategoryArr}
          categoryArr={categoryArr}
          setCategoryModal={setAddCategoryModalOpen}
          listCategory={getCategory}
          FnAftrClose={undefined}
        />
      )}

      {/* Add Category Modal */}
      {addCategoryModalOpen && (
        <AddCategoryModal
          open={addCategoryModalOpen}
          setOpen={setAddCategoryModalOpen}
          MainParent={type}
          setCheckedLabel={(id: number) => {
            setPrtDetails(id);
            onChange(id);
          }}
          setStateS={setState}
          params={params}
          listCategory={getCategory}
          dataArr={dataArr}
          setCategoryData={(data: CreateItemCategorymasterMutation['createItemCategorymaster']) => {
            if (setCategoryData) {
              setCategoryData(data);
            }
            // Store newly created category info temporarily until refetch completes
            if (data?.itemcategoryid && data?.category) {
              setNewlyCreatedCategory({
                id: data.itemcategoryid,
                name: data.category,
              });
              // Auto-select the newly created category
              onChange(data.itemcategoryid);
              // Close the dropdown after successful add/select.
              handleClose();
              // Clear the temporary category after refetch completes (handled by useEffect below)
            }
          }}
          IDparent={parentId ? { id: parentId } : undefined}
        />
      )}
    </Box>
  );
};

export default CategoryAutocomplete;
